import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Modal, Form, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { storage, db } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, Timestamp } from 'firebase/firestore';

function StudentMaterials({ studentId, studentName }) {
	const [materials, setMaterials] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

	// Upload form state
	const [uploadForm, setUploadForm] = useState({
		file: null,
		title: '',
		type: 'sheet-music',
		description: ''
	});

	useEffect(() => {
		if (studentId) {
			fetchMaterials();
		}
	}, [studentId]);

	const fetchMaterials = async () => {
		try {
			setLoading(true);

			// Fetch materials metadata from Firestore
			const materialsQuery = query(
				collection(db, 'studentMaterials'),
				where('studentId', '==', studentId)
			);
			const snapshot = await getDocs(materialsQuery);
			const materialsData = snapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			}));

			setMaterials(materialsData);
		} catch (error) {
			console.error('Error fetching materials:', error);
			showAlert('Error loading materials', 'danger');
		} finally {
			setLoading(false);
		}
	};

	const showAlert = (message, variant = 'success') => {
		setAlert({ show: true, message, variant });
		setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
	};

	const handleFileSelect = (e) => {
		const file = e.target.files[0];
		if (file) {
			// Auto-populate title from filename
			const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
			setUploadForm(prev => ({
				...prev,
				file,
				title: prev.title || fileName
			}));
		}
	};

	const handleUpload = async (e) => {
		e.preventDefault();

		if (!uploadForm.file) {
			showAlert('Please select a file', 'danger');
			return;
		}

		setUploading(true);
		setUploadProgress(0);

		try {
			// Create storage reference
			const timestamp = Date.now();
			const fileName = `${timestamp}_${uploadForm.file.name}`;
			const storageRef = ref(storage, `students/${studentId}/${uploadForm.type}/${fileName}`);

			// Upload file
			const uploadTask = uploadBytesResumable(storageRef, uploadForm.file);

			uploadTask.on(
				'state_changed',
				(snapshot) => {
					// Track upload progress
					const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					setUploadProgress(progress);
				},
				(error) => {
					console.error('Upload error:', error);
					showAlert('Error uploading file: ' + error.message, 'danger');
					setUploading(false);
				},
				async () => {
					// Upload complete, get download URL
					const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

					// Save metadata to Firestore
					await addDoc(collection(db, 'studentMaterials'), {
						studentId,
						studentName: studentName || 'Unknown Student',
						title: uploadForm.title,
						type: uploadForm.type,
						description: uploadForm.description,
						fileName: uploadForm.file.name,
						fileSize: uploadForm.file.size,
						fileType: uploadForm.file.type,
						storagePath: storageRef.fullPath,
						downloadURL,
						uploadedAt: Timestamp.now()
					});

					showAlert('File uploaded successfully!', 'success');
					setShowUploadModal(false);
					setUploadForm({
						file: null,
						title: '',
						type: 'sheet-music',
						description: ''
					});
					setUploadProgress(0);
					setUploading(false);

					// Refresh materials list
					fetchMaterials();
				}
			);
		} catch (error) {
			console.error('Error uploading:', error);
			showAlert('Error uploading file: ' + error.message, 'danger');
			setUploading(false);
		}
	};

	const handleDelete = async (material) => {
		if (!window.confirm(`Are you sure you want to delete "${material.title}"?`)) {
			return;
		}

		try {
			// Delete from Storage
			const storageRef = ref(storage, material.storagePath);
			await deleteObject(storageRef);

			// Delete from Firestore
			await deleteDoc(doc(db, 'studentMaterials', material.id));

			showAlert('File deleted successfully', 'success');
			fetchMaterials();
		} catch (error) {
			console.error('Error deleting file:', error);
			showAlert('Error deleting file: ' + error.message, 'danger');
		}
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
	};

	const getTypeIcon = (type) => {
		const icons = {
			'sheet-music': 'fa-music',
			'audio-track': 'fa-headphones',
			'video': 'fa-video',
			'document': 'fa-file-alt',
			'other': 'fa-file'
		};
		return icons[type] || icons.other;
	};

	const getTypeBadge = (type) => {
		const badges = {
			'sheet-music': 'primary',
			'audio-track': 'success',
			'video': 'danger',
			'document': 'info',
			'other': 'secondary'
		};
		return badges[type] || badges.other;
	};

	return (
		<Card className='shadow-sm'>
			<Card.Header className='bg-transparent d-flex justify-content-between align-items-center'>
				<h5 className='mb-0'>
					<i className='fa-sharp fa-solid fa-folder-open me-2' style={{ color: '#384971' }}></i>
					Student Materials
				</h5>
				<Button
					variant='primary'
					size='sm'
					onClick={() => setShowUploadModal(true)}
				>
					<i className='fa-sharp fa-solid fa-upload me-2'></i>
					Upload File
				</Button>
			</Card.Header>
			<Card.Body>
				{alert.show && (
					<Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
						{alert.message}
					</Alert>
				)}

				{loading ? (
					<div className='text-center py-4'>
						<Spinner animation='border' variant='primary' />
						<p className='mt-2 text-muted'>Loading materials...</p>
					</div>
				) : materials.length === 0 ? (
					<div className='text-center py-5 text-muted'>
						<i className='fa-sharp fa-solid fa-folder-open fa-3x mb-3 d-block' style={{ opacity: 0.3 }}></i>
						<p>No materials uploaded yet</p>
						<Button variant='primary' onClick={() => setShowUploadModal(true)}>
							Upload First File
						</Button>
					</div>
				) : (
					<div className='table-responsive'>
						<Table hover className='mb-0'>
							<thead className='table-light'>
								<tr>
									<th>Title</th>
									<th>Type</th>
									<th>File</th>
									<th>Size</th>
									<th>Uploaded</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{materials.map((material) => (
									<tr key={material.id}>
										<td className='align-middle'>
											<div>
												<strong>{material.title}</strong>
												{material.description && (
													<>
														<br />
														<small className='text-muted'>{material.description}</small>
													</>
												)}
											</div>
										</td>
										<td className='align-middle'>
											<Badge bg={getTypeBadge(material.type)}>
												<i className={`fa-sharp fa-solid ${getTypeIcon(material.type)} me-1`}></i>
												{material.type.replace('-', ' ')}
											</Badge>
										</td>
										<td className='align-middle'>
											<small className='text-muted'>{material.fileName}</small>
										</td>
										<td className='align-middle'>
											<small>{formatFileSize(material.fileSize)}</small>
										</td>
										<td className='align-middle'>
											<small className='text-muted'>
												{material.uploadedAt?.toDate().toLocaleDateString()}
											</small>
										</td>
										<td className='align-middle'>
											<div className='d-flex gap-2'>
												<Button
													variant='outline-primary'
													size='sm'
													href={material.downloadURL}
													target='_blank'
													title='Download'
												>
													<i className='fa-sharp fa-solid fa-download'></i>
												</Button>
												<Button
													variant='outline-danger'
													size='sm'
													onClick={() => handleDelete(material)}
													title='Delete'
												>
													<i className='fa-sharp fa-solid fa-trash'></i>
												</Button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					</div>
				)}
			</Card.Body>

			{/* Upload Modal */}
			<Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size='lg'>
				<Modal.Header closeButton>
					<Modal.Title>Upload Student Material</Modal.Title>
				</Modal.Header>
				<Form onSubmit={handleUpload}>
					<Modal.Body>
						<Form.Group className='mb-3'>
							<Form.Label>File <span className='text-danger'>*</span></Form.Label>
							<Form.Control
								type='file'
								onChange={handleFileSelect}
								accept='.pdf,.mp3,.wav,.m4a,.mp4,.mov,.doc,.docx,.png,.jpg,.jpeg'
								required
								disabled={uploading}
							/>
							<Form.Text className='text-muted'>
								Accepted: PDF, Audio (MP3, WAV, M4A), Video (MP4, MOV), Documents, Images
							</Form.Text>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Title <span className='text-danger'>*</span></Form.Label>
							<Form.Control
								type='text'
								value={uploadForm.title}
								onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
								placeholder='e.g., "Amazing Grace - Sheet Music"'
								required
								disabled={uploading}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Type <span className='text-danger'>*</span></Form.Label>
							<Form.Select
								value={uploadForm.type}
								onChange={(e) => setUploadForm(prev => ({ ...prev, type: e.target.value }))}
								required
								disabled={uploading}
							>
								<option value='sheet-music'>Sheet Music</option>
								<option value='audio-track'>Audio Track</option>
								<option value='video'>Video</option>
								<option value='document'>Document</option>
								<option value='other'>Other</option>
							</Form.Select>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Description (Optional)</Form.Label>
							<Form.Control
								as='textarea'
								rows={2}
								value={uploadForm.description}
								onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
								placeholder='Add notes about this file...'
								disabled={uploading}
							/>
						</Form.Group>

						{uploading && (
							<div className='mt-3'>
								<ProgressBar
									now={uploadProgress}
									label={`${Math.round(uploadProgress)}%`}
									animated
									striped
								/>
								<small className='text-muted d-block mt-2 text-center'>
									Uploading... Please wait
								</small>
							</div>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button variant='secondary' onClick={() => setShowUploadModal(false)} disabled={uploading}>
							Cancel
						</Button>
						<Button variant='primary' type='submit' disabled={uploading || !uploadForm.file}>
							{uploading ? 'Uploading...' : 'Upload File'}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
		</Card>
	);
}

export default StudentMaterials;
